import { Box, Stack } from "@mui/material";
import { css, keyframes } from "@emotion/react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Color,
  FontStyle,
  JobClickType,
  JobDisplays,
  READY_EMPLOYER,
  Slug,
  VideoOrientation,
} from "../../constants";
import {
  getExperienceTag,
  jobButtonStyle,
  JobFontSize,
  JobTag,
  TaggedRow,
} from "../../utils/jobUtils";
import * as apiService from "../../service/ApiService";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FavHeartContainer from "../FavHeartContainer";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import DescriptionOverlay from "./DescriptionOverlay";
import { useDispatch, useSelector } from "react-redux";
import { jobIdToRaw, jobIdToUrl, jobIsFetti } from "../../utils/utils";
import StandardDescription from "./StandardDescription";
import QuillDescription from "./QuillDescription";
import JobFeatureVid from "./JobFeatureVid";
import { AdBanner } from "./JobVerticalVideoAd";
import FettiCompanyLogo from "../FettiCompanyLogo";
import JobDetailsMenu from "./JobDetailsMenu";

const fadeSec = 2.4;
const fadeAnimation = keyframes`
0% {
    opacity: 0;
}
16% {
    opacity: 1;
}
50% {
    opacity: 1;
}
100% {
    opacity: 0;
}`;
const windowMinusNavbar = "calc(100vh - 200px)";
const styles = {
  outer: {
    width: "100%",
    height: "100%",
    position: "relative",
    // position: "sticky",
    // top: "12px",
    // zIndex: 100,
  },
  container: {
    width: "100%",
    // height: '100%',
    borderRadius: JobDisplays.borderRadius,
    backgroundColor: JobDisplays.backgroundColor,
    boxShadow: JobDisplays.boxShadow,
    padding: JobDisplays.padding,
    ...FontStyle.body,
    minWidth: "",
    maxHeight: windowMinusNavbar,
    // overflow: "hidden",
    // textOverflow: "ellipsis",
    overflowY: "scroll",
  },
  compLogo: {
    backgroundColor: "#ddd",
    borderRadius: "10px",
    width: "50px",
    minWidth: "50px",
    height: "50px",
    display: "flex",

    fontSize: "30px",
    justifyContent: "center",
    alignItems: "center",
    userSelect: "none",
  },
  compName: {
    fontSize: JobFontSize.title,
    color: "#000",
    textAlign: "left",
    fontWeight: "bold",
  },
  longDescription: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: "100%",
    fontSize: JobFontSize.medium,
    color: "#888",
  },
  inner: {
    width: "100%",
  },
  sectionLabel: {
    ...FontStyle.headingBody,
  },
  sectionBody: {
    ...FontStyle.body,
    fontSize: JobFontSize.medium,
  },
  applyButton: {
    ...jobButtonStyle,
  },
  blueExpand: {
    ...FontStyle.headingBody,
    fontSize: JobFontSize.medium,
    color: "#0672cb",
    cursor: "pointer",
    // textDecoration: "underline",
    paddingTop: "10px",
    "&:hover": {
      color: "#1033CC",
    },
  },
};

const InnerStack = ({ children }) => {
  return (
    <Stack sx={styles.inner} direction="column" spacing={1}>
      {children}
    </Stack>
  );
};

const UrlCopy = ({ url, handleClick }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <Stack
      direction="row"
      sx={{
        width: "100%",
        maxWidth: "300px",
        minWidth: "",
        backgroundColor: "#ddd",
        borderRadius: "4px",
        height: "40px",
        px: "10px",
      }}
      // justifyContent='space-between'
      alignItems="center"
      spacing={1}
      onMouseEnter={() => setHover(true)}
      // onMouseLeave={() => setHover(false)}
    >
      <Box
        sx={{
          ...FontStyle.body,
          // overflow: 'hidden',
          // overflow: 'scroll',
          overflow: hover ? "scroll" : "hidden",
          whiteSpace: "nowrap",
          // textOverflow: 'ellipsis',
          textOverflow: hover ? "clip" : "ellipsis",
          maxWidth: "100%",
          minWidth: "",
          fontSize: JobFontSize.medium,
          color: "#888",
        }}>
        {url}
      </Box>
      <ContentCopyIcon
        sx={{
          color: "#888",
          width: "20px",
          height: "20px",
          cursor: "pointer",
          "&:hover": {
            // color: Color.primary,
            color: "#000",
          },
          "&:active": {
            color: "#00f",
          },
        }}
        onClick={() => {
          handleClick();
          navigator.clipboard.writeText(url);
        }}
      />
    </Stack>
  );
};

const collapseColor = "#aaa";
const collapseSize = 24;
const CollapseButton = ({ handleClick }) => {
  // const [hovered, setHovered] = useState(false);
  return (
    <ExpandCircleDownIcon
      // onMouseEnter={() => setHovered(true)}
      // onMouseLeave={() => setHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        handleClick();
      }}
      sx={{
        cursor: "pointer",
        position: "absolute",
        top: "16px",
        // right: "44px",
        right: "16px",
        width: `${collapseSize}px`,
        height: `${collapseSize}px`,
        backgroundColor: "transparent",
        // borderRadius: "4px",
        // border: `2px solid ${collapseColor}`,
        color: collapseColor,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        transition: "all 0.2s ease",
        "&:hover": {
          color: "#888",
        },
      }}
    />
  );
};

// both mobile and focusMode will cause height to be max
// useWindowHeight will allow height to be max, but also allow inner element scrolling
const JobDetails = ({
  job,
  mobile,
  focusMode,
  useWindowHeight,
  fav,
  setFav,
  onMobileCollapse,
  showDescriptionOverlay = "",
  upperElementsHeight = 0,
  parentName = "",
  showPortraitAd = false,
}) => {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const scrollRef = React.useRef(null);
  const [showMore, setShowMore] = React.useState(false);
  const [recentlySet, setRecentlySet] = React.useState(false);
  const [firstJobSet, setFirstJobSet] = React.useState(false);
  const shouldShowMore = showMore;
  // const shouldShowMore = showMore || focusMode;
  useEffect(() => {
    setShowMore(false);
    scrollRef.current.scroll({ top: 0, behavior: "smooth" });
    // this.scrollTo(0, 0);
    // scroll({ top: 0, behavior: "smooth" });
    // scrollRef.current.scrollIntoView({ behavior: "smooth" });
    // window.scrollTo({ top: 0, behavior: "smooth" });
  }, [job]);
  useEffect(() => {
    if (!firstJobSet) {
      setFirstJobSet(true);
      if (!mobile) return;
    }
    if (recentlySet) return;
    setRecentlySet(true);
    setTimeout(() => {
      setRecentlySet(false);
    }, fadeSec * 1000);
  }, [job?.url]);
  if (!job) return null;
  const {
    jobId,
    company,
    jobTitle,
    location,
    locations,
    roleToken,
    experienceLevel,
    jobDescription,
    url,
    vidUrl,
    vidSize,
  } = job;
  const isFetti = jobIsFetti(jobId);
  const logJobClick = ({ clickType }) => {
    apiService.jobClick(dispatch, {
      ...auth.accountData,
      clickType: parentName + "-" + clickType,
      jobId,
    });
  };
  return (
    <Box sx={styles.outer}>
      <DescriptionOverlay visible={showDescriptionOverlay} name="jobDetails" />
      {/* todo: turn into menu */}
      {/* {mobile && <CollapseButton handleClick={onMobileCollapse} />}
      {mobile && (
        <FavHeartContainer
          fav={fav}
          setFav={setFav}
          jobId={jobId}
          rightOffset="44px"
        />
      )} */}

      {mobile && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing="-3px"
          sx={{
            position: "absolute",
            top: JobDisplays.padding,
            right: "10px", // less padding because icon does not fill out its container
            // zIndex: 100,
          }}>
          <FavHeartContainer fav={fav} setFav={setFav} jobId={jobId} />
          <JobDetailsMenu
            job={job}
            fav={fav}
            setFav={setFav}
            canCollapse
            handleCollapse={onMobileCollapse}
          />
        </Stack>
      )}
      <Box
        sx={{
          pointerEvents: "none",
          width: "100%",
          height: "100%",
          zIndex: 100,
          // backgroundColor: "#0ff3",
          position: "absolute",
          top: 0,
          left: 0,
          borderRadius: JobDisplays.borderRadius,
          boxShadow:
            "0px 0px 10px #0AE9F080, 0px 0px 10px #0110FA80, 0px 0px 5px #0110FA80",
          opacity: mobile ? 0 : 1,
          // below is the old animation click
          // disabled, because now it should always glow (non mobile)
          // opacity: 0,
          // ...(recentlySet && {
          //   animation: `${fadeAnimation} ${fadeSec}s cubic-bezier(.84,.18,.53,.59)`,
          // }),
        }}
      />
      <Stack
        sx={{
          ...styles.container,
          ...(mobile && { maxHeight: "fit-content" }),
          ...(focusMode && { maxHeight: "fit-content" }),
          ...(useWindowHeight && {
            maxHeight: `calc(100vh - ${upperElementsHeight + 40}px)`,
          }),
        }}
        direction="column"
        spacing={2}
        ref={scrollRef}>
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          width="100%"
          spacing={1}>
          {/* <Box sx={styles.compLogo}>💸</Box> */}
          <Stack
            direction="column"
            sx={{
              minWidth: 0,
              ...(mobile && { paddingRight: "62px" }),
              width: "100%",
            }}
            spacing={0.5}>
            <Stack
              direction="row"
              alignItems="flex-start"
              sx={{ width: "100%" }}
              spacing={1}
              justifyContent="space-between">
              <Box sx={styles.compName}>{jobTitle}</Box>
              {isFetti && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ paddingTop: "4px" }}>
                  <FettiCompanyLogo jobId={jobIdToRaw(jobId)} size={20} />
                  {/* <FettiCompanyLogo jobId={14} size={20} /> */}
                </Stack>
              )}
            </Stack>
            {/* <Box sx={styles.compName}>{company}</Box> */}
            <Stack direction="column" sx={{ minWidth: 0 }}>
              <Box sx={styles.sectionBody}>{company}</Box>
              <Box sx={styles.sectionBody}>
                {locations ? locations.join(" • ") : location}
              </Box>
            </Stack>
          </Stack>
        </Stack>

        {/* Skill tags */}
        <InnerStack>
          <Box sx={styles.sectionLabel}>Skills and Expertise</Box>
          <Stack
            direction="row"
            width="100%"
            sx={
              {
                // overflow: 'hidden',
              }
            }
            flexWrap="wrap"
            gap={1}>
            {/* <JobTag tag={location} /> */}
            {isFetti ? (
              <JobTag tag={roleToken} nowrap={false} />
            ) : (
              <JobTag
                tag={getExperienceTag({ roleToken, experienceLevel })}
                nowrap={false}
              />
            )}
            <JobTag tag={jobTitle} nowrap={false} />
          </Stack>
        </InnerStack>

        {/* {vidUrl && vidSize === VideoOrientation.landscape && ( */}
        {vidUrl &&
          (vidSize === VideoOrientation.landscape || showPortraitAd) &&
          READY_EMPLOYER && (
            <>
              <AdBanner company={company} />
              <Stack
                sx={{ width: "100%" }}
                direction="row"
                justifyContent="center">
                <Box
                  sx={{
                    width:
                      vidSize === VideoOrientation.landscape ? "100%" : "70%",
                    maxWidth:
                      vidSize === VideoOrientation.landscape
                        ? "unset"
                        : "280px",
                  }}>
                  <JobFeatureVid vidUrl={vidUrl} vidSize={vidSize} />
                </Box>
              </Stack>
            </>
          )}

        {/* Main Job Description */}
        <InnerStack>
          <Box sx={styles.sectionLabel}>Project Overview</Box>
          {!isFetti ? (
            <StandardDescription
              jobDescription={jobDescription}
              setShowMore={setShowMore}
              shouldShowMore={shouldShowMore}
              logJobClick={logJobClick}
              url={url}
              jobId={jobId}
            />
          ) : (
            <QuillDescription
              jobDescription={jobDescription}
              setShowMore={setShowMore}
              shouldShowMore={shouldShowMore}
              logJobClick={logJobClick}
              url={url}
              jobId={jobId}
            />
          )}
        </InnerStack>

        {/* Bottom row with url + apply button */}
        <Stack
          direction="row"
          alignItems="center"
          width="100%"
          spacing={1}
          sx={{ position: "relative" }}>
          {(!isFetti || url) && (
            <UrlCopy
              url={url}
              handleClick={() => logJobClick({ clickType: JobClickType.copy })}
            />
          )}
          <Box
            onClick={() => {
              logJobClick({ clickType: JobClickType.applyNow });
              if (!isFetti || url) {
                window.open(url, "_blank");
              } else {
                // navigate({
                //   pathname: Slug.apply,
                //   search: `?jobId=${jobIdToUrl(jobId)}`,
                // });
                window.open(
                  `${Slug.apply}/?jobId=${jobIdToUrl(jobId)}`,
                  "_blank"
                );
              }
            }}
            sx={styles.applyButton}>
            Apply Now
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

export default JobDetails;
